<template>
  <Header>
    <HeaderBackButton v-if="hasBackButton" :fallback-route="backRoute || '/'" />

    <div v-if="customSection || currentSection" class="page-header-section">
      <AwBadge
        style="padding: 0px; display: flex; align-items: center; justify-content: center"
        size="sm"
        variant="solid"
        :color="(customSection || currentSection)!.section.color"
        class="page-header-badge"
      >
        <AwIcon
          :name="`${navButtonIcon || customSection?.item.icon || currentNavButtonIcon || currentSection!.item.icon}-fill`"
          class="page-header-badge-icon"
          color="white"
        />
      </AwBadge>
      <div class="page-header-breadcrumb">
        <NuxtLink
          v-if="breadcrumbItem && currentSection"
          :to="currentSection.item.to"
          class="page-header-section-name page-header-section-link"
        >
          {{ pageTitle }}
        </NuxtLink>
        <span v-else class="page-header-section-name">{{ pageTitle }}</span>
        <div v-if="breadcrumbItem">
          <AwIcon name="i-ph-caret-right" class="page-header-separator" />
        </div>
        <span v-if="breadcrumbItem" class="page-header-item-name">
          <div v-if="!breadcrumbLoading">
            <TextEllipsis>
              {{ breadcrumbItem }}
            </TextEllipsis>
          </div>
          <div v-else class="flex flex-row gap-2">
            <AwSkeleton class="w-20 h-6" />
          </div>
        </span>
      </div>

      <div v-if="showFavoriteButton">
        <AwButton
          size="sm"
          variant="ghost"
          :color="isCurrentPageFavorite ? 'yellow' : 'gray'"
          class="page-header-favorite-button"
          @click="toggleFavorite"
        >
          <AwIcon name="i-ph-star-fill" class="size-4" />
        </AwButton>
      </div>
    </div>

    <div v-else-if="fallbackIcon || fallbackTitle" class="page-header-fallback">
      <HeaderIcon v-if="fallbackIcon" :icon="fallbackIcon" />
      <HeaderTitle v-if="fallbackTitle">{{ fallbackTitle }}</HeaderTitle>
    </div>

    <slot></slot>
  </Header>
</template>

<script setup lang="ts">
import { useFavorites } from "~/composables/useFavorites";
import { useNavBarData } from "~/composables/useNavBarData";
import type { NavButtonSection, NavButton } from "~/composables/useNavBarData";

const props = defineProps<{
  fallbackIcon?: string;
  fallbackTitle?: string;
  hasBackButton?: boolean;
  backRoute?: string;
  customSection?: { section: NavButtonSection; item: NavButton };
  breadcrumbItem?: string;
  breadcrumbLoading?: boolean;
  pageTitle?: string;
  navButtonIcon?: string;
}>();

const route = useRoute();
const { isExactNavRoute, getCurrentSection } = useNavBarData();
const { isFavorite, toggleFavorite: toggleFavoriteComposable } = useFavorites();

const currentSection = props.customSection || getCurrentSection();
const currentNavButtonIcon = props.navButtonIcon || currentSection?.item.icon;

const showFavoriteButton = computed(() => {
  return isExactNavRoute(route.path);
});

const isCurrentPageFavorite = computed(() => {
  const basePath = route.path.split("?")[0];
  return isFavorite(basePath);
});

const toggleFavorite = () => {
  if (!currentSection) return;

  const basePath = route.path.split("?")[0];

  const favoriteItem = {
    to: basePath,
    icon: props.navButtonIcon || currentSection.item.icon,
    label: props.pageTitle || currentSection.item.label,
    sectionName: currentSection.item.label,
    sectionColor: currentSection.section.color,
    sectionIcon: currentSection.section.icon,
  };

  const wasFavorite = isFavorite(basePath);

  toggleFavoriteComposable(favoriteItem);

  if (wasFavorite && route.query.favorite === "true") {
    navigateTo(basePath);
  }
};

const pageTitle = computed(() => {
  if (props.pageTitle) {
    return props.pageTitle;
  }
  if (props.customSection) {
    return props.customSection.item.label;
  }
  return getCurrentSection()?.item.label || "";
});

defineEmits<{ onPrevious: [] }>();
</script>

<style scoped lang="scss">
.page-header-section {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-header-badge {
  width: 17px;
  height: 17px;
}

.page-header-badge-icon {
  width: 10px;
  height: 10px;
}

.page-header-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-header-section-name {
  color: var(--gray-2);
  font-size: 16px;
  font-weight: 500;
}

.page-header-section-link:hover {
  color: var(--gray-1);
}

.page-header-separator {
  color: var(--gray-2);
  width: 10px;
  height: 10px;
}

.page-header-item-name {
  color: var(--gray-1);
  font-size: 15px;
  font-weight: 500;
  max-width: 250px;
}

.page-header-fallback {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-header-favorite-button {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  transition: all 0.2s ease;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--gray-6) !important;
  }
}
</style>
